import swal from "sweetalert2";
import q from "q";
import { useI18n } from "vue-i18n";
import { api } from "@/services/Api";
import { ApiResult } from "@/services/ApiResult";
import { AwaitTaskResponse } from "@/models/Interfaces";
import {
  PrintSize,
  PrintingType as PrintingTypeEnum,
  ToOptions,
  convertArrayToObject,
} from "@/models/Enums";
import { isElectron } from "@/utils/electron";
import { printPdf } from "@/utils/printer";

export default function () {
  const { t } = useI18n();
  const printingTypes = ToOptions(PrintingTypeEnum, "printing-type.");
  const printingTypeOptions = convertArrayToObject(printingTypes, "id", "name");

  async function awaitTask(
    action: () => Promise<ApiResult<AwaitTaskResponse>>,
    onSuccess: (url: string) => void,
    waitTime = 3000
  ) {
    swal.fire(t("swalAction.processing"));
    swal.showLoading();
    await q.delay(waitTime);

    const resp = await action();
    if (resp.errorMessage) {
      await swal.fire({
        icon: "error",
        text: resp.errorMessage,
      });
      return;
    }
    if (resp.data?.url) {
      onSuccess(resp.data.url);
      return;
    }

    if (waitTime > 0) {
      awaitTask(action, onSuccess, waitTime);
    }
  }

  async function printTicket(orderId: number, printSize: PrintSize = PrintSize.Check_8cm) {
    const { value: type } = await swal.fire({
      title: t("cashier.print-ticket.ticket-type"),
      input: "radio",
      inputOptions: printingTypeOptions,
      customClass: {
        confirmButton: "popup-bookit-button",
        cancelButton: "close-button-wrapper popup-bookit-button",
        input: "swal-cashier-radio",
      },
      buttonsStyling: false,
      showCancelButton: true,
      confirmButtonText: t("cashier.label.yes"),
      cancelButtonText: t("cashier.label.no"),
      inputValidator: (value) => {
        return new Promise((resolve) => {
          if (!value) {
            resolve(t("cashier.print-ticket.select-type"));
          } else {
            resolve("");
          }
        });
      },
    });
    if (!type) {
      return;
    }
    await q.delay(200);
    swal.showLoading();
    const responsePromise = api.CreateOrderTicketJob(orderId, Number(type), printSize);
    await q.delay(400);
    const response = await responsePromise;
    if (response.errorMessage) {
      swal.fire({
        icon: "error",
        title: t("error-pop-up.oops"),
        text: t(`${response.errorMessage}`),
        confirmButtonText: t("cashier.label.ok"),
      });
      return;
    }
    swal.close();
    if (!response.data) {
      return;
    }
    const taskId = response.data.taskId;
    awaitTask(
      () => api.waitExportTask(taskId),
      async (url) => {
        if (isElectron()) {
          await api.PrintOrderTicketByUrl(url);
        }
        else {
          await printPdf(url);
        }
        swal.close();
      }
    );
  }

  async function printReceipt(jobId: number, pdfUrl = "") {
    const result = await swal.fire({
      text: t("cashier.print-receipt"),
      icon: "warning",
      iconColor: "",
      showCancelButton: true,
      cancelButtonText: t("cashier.label.no"),
      confirmButtonText: t("cashier.label.yes"),
      customClass: {
        confirmButton: "popup-bookit-button",
        cancelButton: "close-button-wrapper popup-bookit-button",
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed) {
      if (pdfUrl) {
        if (isElectron()) {
          const response = await api.PrintReceipt(pdfUrl);
        }
        else {
          await printPdf(pdfUrl);
        }
        return pdfUrl;
      } else {
        await awaitTask(
          () => api.waitExportTask(jobId),
          async (url) => {

            if (isElectron()) {
              const response = await api.PrintReceipt(url);
            }
            else {
              await printPdf(url);
            }
            return url;
          }
        );
      }
    }
    return "";
  }

  async function openCashDrawerPrinter(): Promise<void> {
      const result = await api.openMoneyDrawer();
  
      if (result.errorMessage) {
          await swal.fire({ icon: 'error', text: result.errorMessage });
          return;
      }
  
      const taskId = result.data;
  
      awaitTask(
      () => api.waitExportTask(taskId!),
          async (url) => {
              await printPdf(url);
              swal.close();
          }
      );
  }

  return {
    printTicket,
    printReceipt,
    awaitTask,
    openCashDrawerPrinter
  };
}
